import React, { useState, useEffect } from "react";
import { Box, TextField, MenuItem, Button, Typography } from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";

import useMicroSlideApi from "../../../api/MicroSlideApi";

// MicroSlide assay model types from
// vitros/MMWS.Api/Lib/ModelDefinitions.cs (AnalyzerAssayModelType).
// WET_* values (20+) are MicroTip and intentionally excluded.
const ASSAY_MODELS = [
    { value: "0", label: "CM (Colorimetric)" },
    { value: "1", label: "CM Blank" },
    { value: "2", label: "PM (Potentiometric)" },
    { value: "3", label: "PM Blank" },
    { value: "4", label: "Two Point Rate" },
    { value: "5", label: "N Point Rate" },
    { value: "6", label: "DM (Dual Wavelength)" },
    { value: "7", label: "DM Blank" },
    { value: "8", label: "IR" },
    { value: "9", label: "REF" }
];

/**
 * Header for a MicroSlide tab. Mirrors MicroTip/Header.js.
 *
 * Props:
 *  - mode:               "response" | "calibrate" | "predict"
 *  - rows, parameters:   forwarded to useMicroSlideApi
 *  - setParameters:      writes the dropdown selection back into shared state
 *                        so the API hook picks up the right eAssayModelType
 *  - extras:             extra payload values (iLevel, iRep, wcf, bcf, splines)
 *  - setResultDataMap:   updater for the per-mode results history
 *  - onRun:              callback invoked when the action button is clicked
 */
export default function Header({
    mode = "response",
    rows,
    parameters,
    setParameters,
    extras,
    setResultDataMap,
    onRun,
    onResult,
    title
}) {
    const [model, setModel] = useState(String(parameters?.eAssayModelType ?? "6"));
    const [description, setDescription] = useState("");

    // Keep dropdown in sync if parameters get updated externally (e.g. parsed
    // from an uploaded parameters file).
    useEffect(() => {
        if (parameters?.eAssayModelType !== undefined) {
            setModel(String(parameters.eAssayModelType));
        }
    }, [parameters?.eAssayModelType]);

    const handleModelChange = (e) => {
        const v = e.target.value;
        setModel(v);
        setParameters?.(prev => ({ ...prev, eAssayModelType: v }));
    };

    const { callResponse, callCalibrate, callPredict, loading, error } =
        useMicroSlideApi(parameters, rows, extras);

    const buttonLabel = {
        response: "Calculate Response",
        calibrate: "Calibrate",
        predict: "Predict"
    }[mode] || "Run";

    const transformResult = (data) => {
        if (!data) return null;

        if (mode === "response") {
            return {
                Response1: data.dResponse1 ?? "-",
                Response2: data.dResponse2 ?? "-",
                FirstDr: data.dFirstDr ?? "-",
                SubDep: data.dSubDepCheckValue ?? "-",
                ConditionCode: data.zConditionCode ?? "",
                Error: data.zError ?? ""
            };
        }
        if (mode === "calibrate") {
            return {
                A0: data.fCalInterceptA0 ?? "-",
                A1: data.fCalSlopeA1 ?? "-",
                A2: data.fCalCurvatureA2 ?? "-",
                A3: data.fCalBlankCorrectionFactorA3 ?? "-",
                Calibrated: data.bABFCalibrated ?? "-",
                ConditionCode: data.zConditionCode ?? "",
                Error: data.zError ?? ""
            };
        }
        // predict: data shape is { predictions: [{ sampleId, response1, response2, concentration, ... }] }
        const preds = data.predictions || [];
        const summary = {
            "Sample Count": preds.length,
            predictions: preds // History.jsx renders this as a table when present
        };
        if (preds.length > 0) {
            summary["First Concentration"] = preds[0].concentration;
            summary["Last Concentration"] = preds[preds.length - 1].concentration;
        }
        return summary;
    };

    const handleSubmit = async () => {
        if (!rows || rows.length === 0) {
            alert("No data available");
            return;
        }
        if (onRun) onRun();

        try {
            let data;
            if (mode === "response") data = await callResponse();
            else if (mode === "calibrate") data = await callCalibrate();
            else data = await callPredict();

            const formatted = transformResult(data);
            if (!formatted) return;

            setResultDataMap?.(prev => {
                const prevData = prev || {};
                const nextRun = Object.keys(prevData).length + 1;
                const finalDescription =
                    description && description.trim() !== ""
                        ? description
                        : `Run ${nextRun}`;

                const newEntry = {
                    run: nextRun,
                    description: finalDescription,
                    result: formatted,
                    rows,
                    parameters
                };
                setResultDataMap(prev => ({
                    ...prev,
                    [nextRun]: newEntry
                }));

                //sessionStorage.setItem(
                //    "microslideHistory_calibrate",
                //    JSON.stringify({
                //        ...resultDataMap,
                //        [nextRun]: newEntry
                //    }));

                const updated = { ...prevData, [nextRun]: newEntry };
                sessionStorage.setItem(`microslideHistory_${mode}`, JSON.stringify(updated));
                return updated;
            });

            // Surface the raw API response to the parent layout for any
            // mode-specific follow-up (e.g. CalibrationLayout navigates to
            // the Prediction tab with A0-A3 in route state).
            if (onResult) onResult(data, formatted);
        } catch (err) {
            console.error("MicroSlide API ERROR:", err);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-start",
                backgroundColor: "#fff"
            }}
        >
            <Box
                sx={{
                    width: "80%",
                    display: "flex",
                    gap: 2,
                    p: 2,
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    alignItems: "stretch"
                }}
            >
                {/* Dropdown */}
                <TextField
                    select
                    label="Math Model"
                   value="4"
                    sx={{
                        flex: 1,
                        minWidth: 140,
                    }}
                    size="medium"
                >
                    <MenuItem value="0">Linear</MenuItem>
                    <MenuItem value="1">Logit4</MenuItem>
                    <MenuItem value="2">Logit5</MenuItem>
                    <MenuItem value="3">Cubic Spline</MenuItem>
                    <MenuItem value="4">K-Model</MenuItem>
                </TextField>
                <TextField
                    select
                    label="Assay Model"
                    value={model}
                    onChange={handleModelChange}
                    sx={{ flex: 1 }}
                    size="medium"
                >
                    {ASSAY_MODELS.map(m => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label={title || "Description"}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ flex: 3 }}
                    size="medium"
                />

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    startIcon={<ScienceIcon />}
                    disabled={loading}
                >
                    {loading ? `${buttonLabel}...` : buttonLabel}
                </Button>

                {error && <Typography color="error">{error}</Typography>}
            </Box>

            <Box sx={{ width: "20%" }} />
        </Box>
    );
}
